import axios from 'axios';
import { debug, error } from '@/utils/logger';

const API_URL = process.env.NEXT_PUBLIC_APP_API_BASE_URL;
const PUBLIC_CARD_IMAGES_URL = `${API_URL}/api/v2/public/card-images`;
const ADMIN_CARD_IMAGES_URL = `${API_URL}/api/v2/admin/card-images`;

export interface CardImage {
  entity_type: 'destination' | 'travel_style';
  entity_id: string;
  image_url: string;
}

export type CardEntityType = CardImage['entity_type'];

const getAuthHeaders = () => {
  const rawAccessToken = typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null;
  if (!rawAccessToken) {
    throw new Error('Authentication token not found.');
  }

  const accessToken = rawAccessToken.startsWith('Bearer ')
    ? rawAccessToken.substring(7)
    : rawAccessToken;

  const headers: Record<string, string> = {
    'Authorization': accessToken,
    'Content-Type': 'application/json',
  };

  const rawIdToken = typeof window !== 'undefined' ? localStorage.getItem('idToken') : null;
  if (rawIdToken) {
    const idToken = rawIdToken.startsWith('Bearer ') ? rawIdToken.substring(7) : rawIdToken;
    headers['x-id-token'] = idToken;
  }

  return headers;
};

/**
 * Fetch all custom card images (public, no auth required).
 */
export async function getCardImages(): Promise<CardImage[]> {
  if (!API_URL) {
    error('[getCardImages] NEXT_PUBLIC_APP_API_BASE_URL is not set');
    return [];
  }
  try {
    debug('[getCardImages] Fetching custom card images');
    const response = await axios.get<{ data: CardImage[] }>(PUBLIC_CARD_IMAGES_URL);
    const list = response.data?.data;
    const images = Array.isArray(list) ? list : [];
    debug('[getCardImages] Response:', { count: images.length });
    return images;
  } catch (err) {
    error('[getCardImages] Error:', err);
    return [];
  }
}

/**
 * Upsert a custom card image (admin only).
 */
export async function upsertCardImage(data: CardImage): Promise<CardImage> {
  if (!API_URL) {
    throw new Error('NEXT_PUBLIC_APP_API_BASE_URL is not set');
  }
  try {
    debug('[upsertCardImage] Upserting:', data);
    const headers = getAuthHeaders();
    const response = await axios.patch<{ data: CardImage }>(ADMIN_CARD_IMAGES_URL, data, { headers });
    const result = response.data?.data;
    if (!result) {
      throw new Error('Invalid response from upsert card image');
    }
    debug('[upsertCardImage] Success:', result);
    return result;
  } catch (err) {
    error('[upsertCardImage] Error:', err);
    throw err;
  }
}

/**
 * Delete a custom card image (admin only). Reverts card to Unsplash default.
 */
export async function deleteCardImage(
  entityType: CardEntityType,
  entityId: string
): Promise<void> {
  if (!API_URL) {
    throw new Error('NEXT_PUBLIC_APP_API_BASE_URL is not set');
  }
  try {
    debug('[deleteCardImage] Deleting:', { entityType, entityId });
    const headers = getAuthHeaders();
    await axios.delete(`${ADMIN_CARD_IMAGES_URL}/${entityType}/${entityId}`, { headers });
    debug('[deleteCardImage] Success');
  } catch (err) {
    error('[deleteCardImage] Error:', err);
    throw err;
  }
}
